import { useEffect, useMemo, useState } from "react";
import Modal from "@/components/base/Modal";
import { SelectField, TextAreaField, TextField } from "@/components/base/FormControls";
import { registerMovement, type MovementType } from "@/lib/inventoryApi";
import { logAudit } from "@/lib/audit";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import type { Branch } from "@/types";
import type { ProductOption } from "@/hooks/useInventory";

interface MovementModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  products: ProductOption[];
  branches: Branch[];
  defaultBranchId: string | null;
  defaultProductId?: string | null;
}

const TYPES: { value: MovementType; label: string; help: string }[] = [
  { value: "entrada", label: "Entrada", help: "Aumenta el stock (compras, devoluciones de cliente)." },
  { value: "salida", label: "Salida", help: "Disminuye el stock (uso interno, mermas, daños)." },
  { value: "ajuste", label: "Ajuste", help: "Establece el stock al valor exacto contado." },
  { value: "inicial", label: "Inventario inicial", help: "Carga el stock de apertura del producto." },
  { value: "correccion", label: "Corrección", help: "Corrige un error de conteo previo." },
];

export default function MovementModal({
  open,
  onClose,
  onSaved,
  products,
  branches,
  defaultBranchId,
  defaultProductId,
}: MovementModalProps) {
  const toast = useToast();
  const { profile } = useAuth();
  const [productId, setProductId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [type, setType] = useState<MovementType>("entrada");
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState("");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const activeBranches = useMemo(() => branches.filter((branch) => branch.active), [branches]);

  useEffect(() => {
    if (!open) return;
    setProductId(defaultProductId ?? "");
    setBranchId(defaultBranchId ?? activeBranches[0]?.id ?? "");
    setType("entrada");
    setQuantity("");
    setReason("");
    setReference("");
    setNotes("");
    setSearch("");
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultProductId, defaultBranchId]);

  const selectedProduct = products.find((product) => product.id === productId);
  const typeHelp = TYPES.find((item) => item.value === type)?.help ?? "";

  const filteredProducts = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = term
      ? products.filter((product) =>
          `${product.name} ${product.sku ?? ""}`.toLowerCase().includes(term),
        )
      : products;
    return list.slice(0, 100);
  }, [products, search]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!productId) {
      setError("Selecciona un producto.");
      return;
    }
    if (!branchId) {
      setError("Selecciona una sucursal.");
      return;
    }
    const qty = Number.parseFloat(quantity.replace(",", "."));
    if (!Number.isFinite(qty) || qty < 0) {
      setError("Ingresa una cantidad válida.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await registerMovement({
        productId,
        branchId,
        movementType: type,
        quantity: qty,
        unit: selectedProduct?.unit ?? null,
        reason: reason.trim() || null,
        reference: reference.trim() || null,
        notes: notes.trim() || null,
        userId: profile?.id ?? null,
      });

      if (!result.ok) {
        setError(result.error ?? "No se pudo registrar el movimiento.");
        return;
      }

      await logAudit({
        userId: profile?.id ?? null,
        userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
        action: "inventario",
        entity: "inventory_movements",
        entityId: productId,
        branchId,
        newValue: { movement_type: type, quantity: qty, new_stock: result.newStock },
      });

      toast.success(`Movimiento registrado. Nuevo stock: ${result.newStock}.`);
      onSaved();
      onClose();
    } catch {
      setError("Ocurrió un error inesperado.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title="Registrar movimiento"
      subtitle="Todo cambio de stock queda registrado y auditado con usuario, sucursal y fecha."
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="movement-form"
            disabled={submitting}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-70"
          >
            {submitting ? <i className="ri-loader-4-line animate-spin text-base"></i> : null}
            Registrar movimiento
          </button>
        </>
      }
    >
      <form id="movement-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div className="flex flex-col gap-3">
          <span className="text-xs font-semibold text-foreground-700">Tipo de movimiento</span>
          <div className="flex flex-wrap gap-2">
            {TYPES.map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => setType(item.value)}
                className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                  type === item.value
                    ? "border-primary-500 bg-primary-500 text-background-50"
                    : "border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-foreground-400">{typeHelp}</p>
        </div>

        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-base"></i>
          </span>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar producto por nombre o SKU..."
            className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-950 outline-none placeholder:text-foreground-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>

        <SelectField
          label="Producto"
          name="product_id"
          value={productId}
          onChange={setProductId}
          placeholder="Selecciona un producto"
          required
          options={filteredProducts.map((product) => ({
            value: product.id,
            label: product.sku ? `${product.name} (${product.sku})` : product.name,
          }))}
        />

        <SelectField
          label="Sucursal"
          name="branch_id"
          value={branchId}
          onChange={setBranchId}
          placeholder="Selecciona una sucursal"
          required
          options={activeBranches.map((branch) => ({ value: branch.id, label: branch.name }))}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label={type === "ajuste" ? "Stock exacto contado" : "Cantidad"}
            name="quantity"
            value={quantity}
            onChange={setQuantity}
            inputMode="numeric"
            placeholder="0"
            required
          />
          <TextField
            label="Referencia"
            name="reference"
            value={reference}
            onChange={setReference}
            placeholder="Factura, orden, documento..."
          />
        </div>

        <TextField
          label="Motivo"
          name="reason"
          value={reason}
          onChange={setReason}
          placeholder="Compra a proveedor, merma, conteo físico..."
        />

        <TextAreaField
          label="Notas"
          name="notes"
          value={notes}
          onChange={setNotes}
          placeholder="Detalles adicionales del movimiento."
          rows={2}
        />

        {error ? (
          <div className="animate-shake flex items-start gap-2.5 rounded-md border border-primary-300 bg-primary-50 px-3.5 py-3 text-sm text-primary-800">
            <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
              <i className="ri-error-warning-line text-base"></i>
            </span>
            <span className="flex-1 leading-snug">{error}</span>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}